import { useEffect, useMemo, useState } from 'react';
import {
  Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tabs, Tooltip, Typography, message,
} from 'antd';
import { DatabaseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import {
  listModuleRecords, createModuleRecordGeneric, updateModuleRecordGeneric, deleteModuleRecordGeneric,
} from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;
const idOf = (record) => record?._id ?? record?.id;

const TAX_TYPE_OPTIONS = [
  { label: 'State Tax', value: 'State Tax' },
  { label: 'Federal Tax', value: 'Federal Tax' },
  { label: 'Sales Tax', value: 'Sales Tax' },
  { label: 'VAT', value: 'VAT' },
  { label: 'Other', value: 'Other' },
];

// TaxEditor — small Add/Edit modal for a single tax entry (name/taxType/taxValue).
function TaxEditor({ open, record, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) form.setFieldsValue({ name: record?.name ?? '', taxType: record?.taxType, taxValue: record?.taxValue });
  }, [open, record, form]);

  async function save() {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (record) await updateModuleRecordGeneric('taxes', idOf(record), values);
      else await createModuleRecordGeneric('taxes', values);
      message.success(record ? 'Tax updated' : 'Tax created');
      onSaved();
    } catch (e) {
      message.error(e?.message || 'Could not save tax');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnHidden
      title={record ? 'Edit Tax' : 'New Tax'}>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Tax Name" rules={[{ required: true, message: 'Name is required' }]}>
          <Input placeholder="e.g. NY Sales Tax" />
        </Form.Item>
        <Form.Item name="taxType" label="Tax Type">
          <Select allowClear options={TAX_TYPE_OPTIONS} placeholder="Select type" />
        </Form.Item>
        <Form.Item name="taxValue" label="Tax Value (%)" rules={[{ required: true, message: 'Tax value is required' }]}>
          <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="8.875" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// LocationEditor — name + linked tax (autofills taxType/taxValue for display,
// mirroring the same autofillFrom convention the submission form uses).
function LocationEditor({ open, record, taxes, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const taxOptions = useMemo(() => taxes.map((t) => ({ label: t.name, value: idOf(t) })), [taxes]);

  useEffect(() => {
    if (open) form.setFieldsValue({ name: record?.name ?? '', taxId: record?.taxId });
  }, [open, record, form]);

  async function save() {
    const values = await form.validateFields();
    const linkedTax = taxes.find((t) => idOf(t) === values.taxId);
    const payload = { ...values, taxType: linkedTax?.taxType ?? '', taxValue: linkedTax?.taxValue ?? undefined };
    setSaving(true);
    try {
      if (record) await updateModuleRecordGeneric('locations', idOf(record), payload);
      else await createModuleRecordGeneric('locations', payload);
      message.success(record ? 'Location updated' : 'Location created');
      onSaved();
    } catch (e) {
      message.error(e?.message || 'Could not save location');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onCancel={onClose} onOk={save} confirmLoading={saving} destroyOnHidden
      title={record ? 'Edit Location' : 'New Location'}>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Location Name" rules={[{ required: true, message: 'Name is required' }]}>
          <Input placeholder="e.g. New York, NY" />
        </Form.Item>
        <Form.Item name="taxId" label="Linked Tax" tooltip="Selecting a location auto-fills this tax rate on the submission form.">
          <Select allowClear showSearch optionFilterProp="label" options={taxOptions} placeholder="Select a tax entry" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function TaxesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    setLoading(true);
    listModuleRecords('taxes').then(({ items: data }) => setItems(data))
      .catch((e) => message.error(e?.message || 'Could not load taxes'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { queueMicrotask(load); }, []);

  async function remove(id) {
    try {
      await deleteModuleRecordGeneric('taxes', id);
      message.success('Tax deleted');
      load();
    } catch (e) {
      message.error(e?.message || 'Could not delete tax');
    }
  }

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Type', dataIndex: 'taxType', key: 'taxType', render: (v) => v || '—' },
    { title: 'Value', dataIndex: 'taxValue', key: 'taxValue', render: (v) => (v != null ? `${v}%` : '—') },
    { title: '', key: 'actions', width: 90, render: (_, record) => (
      <Space size={4}>
        <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditing(record); setEditorOpen(true); }} /></Tooltip>
        <Popconfirm title="Delete this tax?" onConfirm={() => remove(idOf(record))}>
          <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
        </Popconfirm>
      </Space>
    ) },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard title={`${items.length} tax entr${items.length === 1 ? 'y' : 'ies'}`}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>Reload</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true); }}>New tax</Button>
          </Space>
        )}>
        <Table rowKey={idOf} columns={columns} dataSource={items} loading={loading} size="small" pagination={false} className="fg-table" />
      </AppCard>
      <TaxEditor open={editorOpen} record={editing} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}

function LocationsPanel() {
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    setLoading(true);
    Promise.all([listModuleRecords('locations'), listModuleRecords('taxes')])
      .then(([locResult, taxResult]) => { setItems(locResult.items); setTaxes(taxResult.items); })
      .catch((e) => message.error(e?.message || 'Could not load locations'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { queueMicrotask(load); }, []);

  async function remove(id) {
    try {
      await deleteModuleRecordGeneric('locations', id);
      message.success('Location deleted');
      load();
    } catch (e) {
      message.error(e?.message || 'Could not delete location');
    }
  }

  const taxNameById = useMemo(() => new Map(taxes.map((t) => [idOf(t), t.name])), [taxes]);

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Linked Tax', key: 'tax', render: (_, record) => taxNameById.get(record.taxId) || <Text type="secondary">None</Text> },
    { title: 'Tax Value', dataIndex: 'taxValue', key: 'taxValue', render: (v) => (v != null ? `${v}%` : '—') },
    { title: '', key: 'actions', width: 90, render: (_, record) => (
      <Space size={4}>
        <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditing(record); setEditorOpen(true); }} /></Tooltip>
        <Popconfirm title="Delete this location?" onConfirm={() => remove(idOf(record))}>
          <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
        </Popconfirm>
      </Space>
    ) },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard title={`${items.length} location${items.length === 1 ? '' : 's'}`}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>Reload</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true); }}>New location</Button>
          </Space>
        )}>
        <Table rowKey={idOf} columns={columns} dataSource={items} loading={loading} size="small" pagination={false} className="fg-table" />
      </AppCard>
      <LocationEditor open={editorOpen} record={editing} taxes={taxes} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}

export default function MastersSection() {
  return (
    <div className="fg-section">
      <AppCard icon={<DatabaseOutlined />} title="Masters">
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Manage the shared Location and Tax lists used across modules (e.g. the submission form&apos;s W2 employer
          section). Selecting a location on a submission auto-fills its linked tax value.
        </Text>
      </AppCard>
      <Tabs defaultActiveKey="locations" className="admin-config-tabs" items={[
        { key: 'locations', label: 'Locations', children: <LocationsPanel /> },
        { key: 'taxes', label: 'Taxes', children: <TaxesPanel /> },
      ]} />
    </div>
  );
}
